import React, { useEffect, useState } from "react";
import Video, { Participant, Room } from "twilio-video";
import "../../style/style.scss";
import { getBrowserName, getParams, getAuthToken, getIdentity } from "../../utils/utils.ts";
import FullScreenLoader, { LoadingProps } from "../../components/full-screen-loader/FullScreenLoader.tsx";
import { showToaster } from "../Toaster.tsx";

const isHost = (participant: Participant) => {
  const identifiableObject = participant.identity && JSON.parse(participant.identity);
  return !!identifiableObject.isHost;
};

const Join = () => {
  const { roomId } = getParams(window.location.href);

  const [loading, setLoading] = useState<LoadingProps>();

  const [room, setRoom] = useState<Room>();

  const setNoVideoError = () => {
    setLoading({
      type: "error",
      subHeading: "No Video available from client side"
    });
  };

  const connectParticipantToRoom = async (authToken: string) => {
    try {
      const videoRoom = await Video.connect(authToken, {
        name: roomId,
        audio: false,
        video: false
      });
      if (!videoRoom) {
        throw new Error("Something went wrong");
      }
      setRoom(videoRoom);
      return videoRoom;
    } catch (err) {
      throw err;
    }
  };

  const onVideoPause = () => {
    const videoContainer = document.getElementById("video-container");
    const vids = document.getElementsByTagName("video");
    if (videoContainer && videoContainer.hasChildNodes() && vids && vids[0]) {
      videoContainer?.removeChild(vids[0]);
    }
    setLoading({
      type: "paused",
      subHeading: "Screen sharing has been paused by the host"
    });
  };

  const onTrackSubscribed = (track: Video.RemoteTrack) => {
    if (track.kind === "video") {
      const videoContainer = document.getElementById("video-container");

      if (videoContainer) {
        const videoElement = track.attach() as HTMLVideoElement;

        if (!track.mediaStreamTrack.enabled) {
          setNoVideoError();
        }

        videoElement.autoplay = true;
        videoElement.playsInline = true;
        videoElement.muted = true;
        videoElement.volume = 1;
        videoElement.style.height = "100vh";
        if (videoContainer.hasChildNodes()) {
          const vid = document.getElementsByTagName("video");
          videoContainer.removeChild(vid[0]);
        }
        videoContainer.appendChild(videoElement);
      }
    }
  };

  const onParticipantJoin = (participant: Video.RemoteParticipant) => {
    if (!isHost(participant)) {
      return;
    }

    // when host share a new video
    participant.on("trackSubscribed", onTrackSubscribed);

    // When host click on stop sharing from OUR UI BUTTON
    participant.on("trackUnpublished", (track) => {
      if (track.kind === "video") {
        onVideoPause();
      }
    });
    participant.on("trackUnsubscribed", async (track) => {
      if (track.kind === "video") {
        if (isHost(participant) && participant.state === "disconnected" && navigator.sendBeacon) {
          navigator.sendBeacon(`${process.env.REACT_APP_SS_BASE_URL}/destroy-room?roomId=${roomId}`);
        } else {
          onVideoPause();
        }
      }
    });

    // When host click on stop sharing from BROWSER'S BANNER
    participant.on("trackDisabled", (track) => {
      if (track.kind === "video") {
        onVideoPause();
      }
    });

    // when the video actually starts playing
    participant.on("trackStarted", (track) => {
      if (track.kind === "video") {
        setLoading(undefined);
      }
    });
  };

  const checkRoomExistence = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SS_BASE_URL}/room-exists?roomId=${roomId}`);
      const { roomExists } = await response.json();
      if (!roomExists) {
        throw new Error("This session is not available anymore");
      }
      return roomExists;
    } catch (err) {
      throw err;
    }
  };

  const joinRoom = async function () {
    /**
     * Steps :
     * 1. Check if roomId is provided
     *    Check if room exist by roomId (Step 1)
     * 2. Create a random unique Identity
     * 3. Fetch token from backend with the Identity (Step 2) and roomId (Step 1)
     * 4.
     * 5. If exist, connect to Twilio Room with the videoToke (Step 3) and roomId (Step 1)
     * 6. Set Participants
     */
    try {
      setLoading({
        subHeading: "We are joining you to the room",
        type: "loading"
      });

      //  Check if roomId is provided
      if (!roomId) {
        setLoading({ subHeading: "No room name provided!", type: "error" });
        return;
      }

      // check if room exist
      await checkRoomExistence();

      // const name = window.prompt("Enter your name");

      const identifiableToken = getIdentity(getBrowserName());

      // Fetch token from backend
      const authToken = await getAuthToken(identifiableToken, roomId);

      const videoRoom = await connectParticipantToRoom(authToken);

      videoRoom.participants.forEach(onParticipantJoin);
      videoRoom.on("participantConnected", onParticipantJoin);
      videoRoom.on("participantReconnecting", (participant) => {
        if (isHost(participant)) {
          setLoading({
            type: "paused",
            subHeading: "Host is reconnecting..."
          });
        }
      });

      videoRoom.on("participantReconnected", (participant) => {
        if (isHost(participant)) {
          setLoading({ type: "none" });
        }
      });

      videoRoom.on("disconnected", (_, error) => {
        console.log(error.code);
        let errorMessage = "This session has been ended by host";
        if ([53405, 53001].includes(error.code)) {
          errorMessage = "Disconnected due to internet connectivity issue. Please try again";
        } else if (error.code === 53216) {
          errorMessage = "Session time limit reached";
        }
        setLoading({
          type: "disconnected",
          subHeading: errorMessage
        });
      });

      videoRoom.on("reconnecting", () => {
        showToaster("Reconnecting due to network issue...");
      });

      videoRoom.on("reconnected", () => {
        showToaster("Reconnected successfully!");
      });

      if (!videoRoom.participants.size) {
        return;
      }

      let hasVideoTrack = false;

      videoRoom.participants.forEach((participant) => {
        if (isHost(participant)) {
          if (participant.videoTracks.size) {
            hasVideoTrack = true;
          }
        }
      });

      if (hasVideoTrack) {
        setLoading({
          subHeading: "Starting Video...",
          type: "loading"
        });
      } else {
        onVideoPause();
      }
    } catch (err) {
      console.error("Error joining the room:", err);
      setLoading({
        subHeading: err?.message ?? "Something went wrong",
        type: "error"
      });
    }
  };

  // join room
  useEffect(() => {
    document.title = "Join";
    joinRoom();
  }, []);

  useEffect(() => {
    window.onbeforeunload = () => {
      const participantSid = room?.localParticipant.sid;
      navigator.sendBeacon(`${process.env.REACT_APP_SS_BASE_URL}/room/${roomId}/remove-participant/${participantSid}`);
    };
    return () => {
      window.onbeforeunload = null;
    };
  }, [room]);

  return (
    <div className="bg-black d-flex align-items-center justify-content-center">
      {loading?.type && loading?.type !== "none" && <FullScreenLoader {...loading} />}
      <div id="video-container"></div>
    </div>
  );
};

export default Join;
